/*
 *   File : edit-personal-details.js
 *   Author : https://evoqins.com
 *   Description : Onboarding step  - Personal details edit page
 *   Integrations : react-toastify, date-fns
 *   Version : 1.0.0
 *   Date : 24-09-2022  
*/

import { useState, useEffect, useRef } from "react";
import { format, subYears } from "date-fns";
import { useSelector } from "react-redux";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";

// *********** Components ***************//  
import { Icon } from "../../../Component/Icon";
import { CustomAmountInput, CustomTextInput, DatePicker, FilePicker, RadioGroup } from "../../../Component/Form-Elements";
import { PrimaryButton } from "../../../Component/Buttons";
import { _checkFileSize, _formatDateFormat, _getSignupProgress, _getUserData, _trackEvent } from "../../../Helper";
import { SelectionSuggestModal } from "../../../Component/Modal";
import { APIService, awsService } from "../../../Service";

// *********** Styles ***************//  
import styles from "../../../Style/Component/login.module.scss";


// *********** Assets ***************//  
import Loader from "../../../Assets/Images/loader.gif";
import Inactive from "../../../Assets/Images/check-box.svg";
import ActiveCheckbox from "../../../Assets/Images/check-box-active.svg";

// *********** Custom Data ***************//
const YES_NO_OPTIONS = [
    {
        id: 1,
        display_name: "Yes"
    },
    {
        id: 2,
        display_name: "No"
    },
];

function PersonalDetails(props) {

    //get general data from redux
    const GENERAL_DATA = useSelector((store) => store.Reducer.GENERAL_DATA);
    const USER_DATA = useSelector((store) => store.Reducer.USER_DATA);
    //get progress data form redux
    const PROGRESS_DATA = useSelector((store) => store.Reducer.SIGNUP_PROGRESS_DATA);
    const [generalData, setGeneralData] = useState(GENERAL_DATA);

    const [name, setName] = useState("");
    const [nameError, setNameError] = useState("");
    const [dob, setDob] = useState("");
    const [dobError, setDobError] = useState("");

    const [genderSelect, setGenderSelect] = useState("");
    const [martialSelect, setMartialSelect] = useState("");
    const [citizenSelect, setCitizenSelect] = useState(1);
    const [taxResidentSelect, setTaxResidentStatus] = useState(2);
    const [selectedOccupation, setSelectedOccupation] = useState([]);
    const [occupationError, setOccupationError] = useState("");
    const [selectedSource, setSelectedSource] = useState([]);
    const [incomeSourceError, setIncomeSourceError] = useState("");
    const [income, setIncome] = useState("");
    const [incomeError, setIncomeError] = useState("");
    const [fatherName, setFatherName] = useState("");
    const [fatherNameError, setFatherNameError] = useState("");
    const [motherName, setMotherName] = useState("");
    const [motherNameError, setMotherNameError] = useState("");
    const [taxPayingCountry, setTaxPayingCountry] = useState([]);
    const [taxPayingCountryError, setTaxPayingCountryError] = useState("");
    const [taxProof, setTaxProof] = useState([]);
    const [taxProofError, setTaxProofError] = useState("");
    const [taxReferenceNumber, setTaxReferenceNumber] = useState("");
    const [taxReferenceNumberError, setTaxReferenceNumberError] = useState("");

    const [stateData, setStateData] = useState([]);
    const [cityData, setCityData] = useState([]);
    const [countryData, setCountryData] = useState(props.countryData);
    const [address, setAddress] = useState("");
    const [addressError, setAddressError] = useState("");
    const [secondAddress, setSecondAddress] = useState("");
    const [secondAddressError, setSecondAddressError] = useState("");
    const [selectedState, setSelectedState] = useState([]);
    const [stateError, setStateError] = useState("");
    const [selectedCity, setSelectedCity] = useState([]);
    const [cityError, setCityError] = useState("");
    const [pinCode, setPinCode] = useState("");
    const [pinCodeError, setPinCodeError] = useState("");
    const [modalType, setModalType] = useState("");
    const [hasDifferentCorrsAddress, setHasDifferentCorrsAddress] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState([]);
    const [countryError, setCountryError] = useState("");

    const [panFile, setPanFile] = useState(null);
    const [panViewUrl, setPanViewUrl] = useState(null);
    const [panFileError, setPanFileError] = useState("");
    const [signFile, setSignFile] = useState(null);
    const [signViewUrl, setSignViewUrl] = useState(null);
    const [signFileError, setSignFileError] = useState("");
    const [personImageFile, setPersonImageFile] = useState(null);
    const [personImageViewUrl, setPersonImageViewUrl] = useState(null);
    const [personImageFileError, setPersonImageFileError] = useState("");
    const [showTaxPayingCountryModal, setShowTaxPayingCountryModal] = useState(false);
    const [showTaxProofModal, setShowTaxProofModal] = useState(false);
    const [showSuggestModal, setShowSuggestModal] = useState(false);
    const [showSourceOfIncomeModal, setShowSourceOfIncomeModal] = useState(false);
    const [showOccupationModal, setShowOccupationModal] = useState(false);
    const [showCountryModal, setShowCountryModal] = useState(false);

    //correspond address
    const [correspondNation, setCorrespondNation] = useState([]);
    const [correspondNationError, setCorrespondNationError] = useState("");
    const [correspondAddressOne, setCorrespondAddressOne] = useState("");
    const [correspondAddressError, setCorrespondAddressError] = useState("");
    const [correspondSecondAddress, setCorrespondSecondAddress] = useState("");
    const [correspondSecondAddressError, setCorrespondSecondAddressError] = useState("");
    const [correspondState, setCorrespondState] = useState("");
    const [correspondIndianState, setCorrespondIndianState] = useState([]);
    const [correspondStateError, setCorrespondStateError] = useState("");
    const [correspondCity, setCorrespondCity] = useState("");
    const [correspondIndianCity, setCorrespondIndianCity] = useState([]);
    const [correspondCityError, setCorrespondCityError] = useState("");
    const [correspondPinCode, setCorrespondPinCode] = useState("");
    const [correspondPinCodeError, setCorrespondPinCodeError] = useState("");

    const [isFileUpload, setIsFileUpload] = useState("");
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);

    const screenViewLatch = useRef(false);

    // Screen view tracking
    useEffect(() => {
        if (screenViewLatch.current === false) {
            screenViewLatch.current = true;
            _trackEvent("screen_personal_details_viewed", { screen: "personal_details", flow: "onboarding" });
        }
    }, []);

    useEffect(() => {
        _getState();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {

        if (USER_DATA?.personal_data.name !== null) {
            setName(USER_DATA?.personal_data?.name);
        };

        if (USER_DATA?.personal_data.dob) {
            setDob(_formatDateFormat(USER_DATA?.personal_data.dob));
        };

        if (USER_DATA?.personal_data.father_name !== null) {
            setFatherName(USER_DATA?.personal_data.father_name);
        };

        if (USER_DATA?.personal_data.mother_name !== null) {
            setMotherName(USER_DATA?.personal_data.mother_name);
        };

        if (USER_DATA?.personal_data.is_indian_citizen == true || USER_DATA?.personal_data.is_indian_citizen == null) {
            setCitizenSelect(1);
        } else {
            setCitizenSelect(2);
        };

        if (USER_DATA?.personal_data.foreign_tax_residence === true) {
            setTaxResidentStatus(1);
        } else {
            setTaxResidentStatus(2);
        };

        if (USER_DATA?.personal_data.occupation_id !== null && USER_DATA?.personal_data.occupation !== null) {
            setSelectedOccupation({ id: USER_DATA?.personal_data.occupation_id, display_name: USER_DATA?.personal_data.occupation });
        };

        if (USER_DATA?.personal_data.income_source_id !== null && USER_DATA?.personal_data.income_source) {
            setSelectedSource({ id: USER_DATA?.personal_data.income_source_id, display_name: USER_DATA?.personal_data.income_source });
        };

        if (USER_DATA?.personal_data.annual_income > 0) {
            setIncome(USER_DATA?.personal_data.annual_income);
        };






        // Address Details
        if (USER_DATA?.address["permanent_address"] !== undefined) {

            if (USER_DATA?.address.permanent_address?.address_line1) {
                setAddress(USER_DATA?.address.permanent_address.address_line1);
            }

            if (USER_DATA?.address.permanent_address?.address_line2) {
                setSecondAddress(USER_DATA?.address.permanent_address.address_line2);
            }

            if (USER_DATA?.address.permanent_address?.state_id) {
                setSelectedState({ id: USER_DATA?.address.permanent_address.state_id, name: USER_DATA?.address.permanent_address.state_name });
            }

            if (USER_DATA?.address.permanent_address?.city_id) {
                setSelectedCity({ id: USER_DATA?.address.permanent_address.city_id, name: USER_DATA?.address.permanent_address.city });
            }

            if (USER_DATA?.address.permanent_address?.pincode) {
                setPinCode(USER_DATA?.address.permanent_address.pincode);
            }

        };

        if (USER_DATA?.address.is_same_corresponding_address === false) {
            if (USER_DATA?.address['corresponding_address'] !== undefined) {

                if (USER_DATA?.address?.corresponding_address?.address_line1) {
                    setCorrespondAddressOne(USER_DATA?.address.corresponding_address.address_line1);
                }

                if (USER_DATA?.address?.corresponding_address?.address_line2) {
                    setCorrespondSecondAddress(USER_DATA?.address.corresponding_address.address_line2);
                }

                if (USER_DATA?.address?.corresponding_address?.country_id) {
                    let country = countryData?.countries_for_address?.find((item) => item.id === USER_DATA?.address?.corresponding_address?.country_id);
                    setCorrespondNation(country || []);
                }

                if (USER_DATA?.address?.corresponding_address?.country_id === 251) {
                    setCorrespondIndianState({ id: USER_DATA?.address?.corresponding_address?.state_id, name: USER_DATA?.address?.corresponding_address?.state_name });
                    setCorrespondIndianCity({ id: USER_DATA?.address?.corresponding_address?.city_id, name: USER_DATA?.address?.corresponding_address?.city });
                } else {
                    if (USER_DATA?.address?.corresponding_address?.state_name) {
                        setCorrespondState(USER_DATA?.address.corresponding_address.state_name);
                    }
                    if (USER_DATA?.address?.corresponding_address?.city) {
                        setCorrespondCity(USER_DATA?.address.corresponding_address.city);
                    }
                }

                if (USER_DATA?.address?.corresponding_address?.pincode) {
                    setCorrespondPinCode(USER_DATA?.address.corresponding_address.pincode);
                }

            }
        }
        setHasDifferentCorrsAddress(!USER_DATA?.address.is_same_corresponding_address || props.isNRI);
        setDataLoading(false);

    }, [USER_DATA]);

    useEffect(() => {
        setGeneralData(GENERAL_DATA);
    }, [GENERAL_DATA]);

    useEffect(() => {
        if (PROGRESS_DATA) {
            let data = PROGRESS_DATA?.kyc_data || {};

            if (data.country_code) {
                setSelectedCountry({ id: data.country_code, name: data.country });
            }

            if (data.tax_country_id) {
                setTaxPayingCountry({ id: data.tax_country_id, name: data.tax_country });
            }

            if (data.reference_proof_id) {
                setTaxProof({ id: data.reference_proof_id, name: data.reference_proof, display_name: data.reference_proof });
            }

            setTaxReferenceNumber(data.tax_reference_number || "");

        }
    }, [PROGRESS_DATA]);



    useEffect(() => {
        if (USER_DATA?.personal_data.gender_id !== null) {
            setGenderSelect(USER_DATA?.personal_data.gender_id);
        } else {
            if (generalData.genders.length > 0) {
                setGenderSelect(generalData.genders[0].id);
            }
        };

        if (USER_DATA?.personal_data.marital_status_id !== null) {
            setMartialSelect(USER_DATA?.personal_data.marital_status_id);
        } else {
            if (generalData.marital_statuses.length > 0) {
                setMartialSelect(generalData.marital_statuses[0].id);
            }
        };

        // uploaded file 
        if (USER_DATA?.personal_data.identity_proof_url !== null) {
            let pan_url = USER_DATA?.personal_data.identity_proof_url;
            setPanFile(pan_url);
            setPanViewUrl(pan_url);
        };

        if (USER_DATA?.personal_data.photo_proof_url !== null) {
            let image_url = USER_DATA?.personal_data.photo_proof_url;
            setPersonImageFile(image_url);
            setPersonImageViewUrl(image_url);
        }

        if (USER_DATA?.personal_data.signature_proof_url !== null) {
            let signature_url = USER_DATA?.personal_data.signature_proof_url;
            setSignFile(signature_url);
            setSignViewUrl(signature_url);
        };

    }, [generalData, USER_DATA]);


    useEffect(() => {

        if (selectedState.id) {
            _getCity(selectedState.id);
        };

    }, [selectedState]);

    useEffect(() => {

        if (correspondIndianState.id) {
            _getCity(correspondIndianState.id);
        };

    }, [correspondIndianState]);


    useEffect(() => {

        if (showOccupationModal === true) {
            let occupation_modal = new Modal(document.getElementById('select-occupation-modal'));
            occupation_modal.show();
        };

    }, [showOccupationModal]);

    useEffect(() => {

        if (showSourceOfIncomeModal === true) {
            let source_modal = new Modal(document.getElementById('select-source-modal'));
            source_modal.show();
        };

    }, [showSourceOfIncomeModal]);

    //  Opening Modal
    useEffect(() => {

        if (showTaxPayingCountryModal === true) {
            let country_modal = new Modal(document.getElementById('select-tax-paying-country-modal'));
            country_modal.show();
        };

    }, [showTaxPayingCountryModal]);

    useEffect(() => {

        if (showTaxProofModal === true) {
            let tax_proof_modal = new Modal(document.getElementById('select-tax-proof-modal'));
            tax_proof_modal.show();
        };

    }, [showTaxProofModal]);


    useEffect(() => {

        if (showSuggestModal === true) {
            if (modalType === 1) {
                let state_modal = new Modal(document.getElementById('select-state-modal'));
                state_modal.show();
            } else if (modalType === 2) {
                let city_modal = new Modal(document.getElementById('select-city-modal'));
                city_modal.show();
            } else if (modalType === 3) {
                let city_modal = new Modal(document.getElementById('select-correspond-country-modal'));
                city_modal.show();
            } else if (modalType === 4) {
                let address_modal = new Modal(document.getElementById('select-address-proof-modal'));
                address_modal.show();
            } else if (modalType === 5) {
                let corrs_state_modal = new Modal(document.getElementById('select-corrs-state-modal'));
                corrs_state_modal.show();
            } else if (modalType === 6) {
                let corrs_city_modal = new Modal(document.getElementById('select-corrs-city-modal'));
                corrs_city_modal.show();
            }
        };

    }, [showSuggestModal]);

    useEffect(() => {

        if (showCountryModal === true) {
            let country_modal = new Modal(document.getElementById('select-country-modal'));
            country_modal.show();
        };

    }, [showCountryModal]);

    // handle name change   
    const _handleNameChange = (value) => {
        setName(value);
        setNameError("");
    };

    //handle change date
    const _handleDateChange = (date) => {
        setDob(date);
        setDobError("");
    };

    //handle gender select
    const _handleGenderSelect = (id) => {
        setGenderSelect(id);
    };

    //handle the martial status
    const _handleMartialSelect = (id) => {
        setMartialSelect(id);
    };

    //handle citizenship
    const _handleCitizenSelect = (id) => {
        setCitizenSelect(id);
    };

    //handle tax
    const _handleTaxSelect = (id) => {
        setTaxResidentStatus(id);
    };

    //handle select occupation
    const _handleSelectOccupation = (value) => {
        setSelectedOccupation(value);
        setOccupationError("");
    };

    //handle select income source
    const _handleSelectSource = (value) => {
        setSelectedSource(value);
        setIncomeSourceError("");
    };

    //handle INcome
    const _handleIncome = (value) => {
        setIncome(value);
        setIncomeError("");
    };

    //handle address change 
    const _handleAddressChange = (value) => {
        setAddress(() => value);
        setAddressError("");
    };

    //handle address 2 change 
    const _handleSecondAddressChange = (value) => {
        setSecondAddress(() => value);
        setSecondAddressError("");
    };

    //handle modal
    const _handleSuggestionModal = (type) => {
        setModalType(type);
        setShowSuggestModal(true);
    };

    const _handlePinChange = (value) => {
        setPinCode(value.toUpperCase());
        setPinCodeError("");
    };


    const _handleSelectTaxPayingCountry = (value) => {
        setTaxPayingCountry(value);
        setTaxPayingCountryError("");
        setShowTaxPayingCountryModal(false);
    };

    const _handleSelectTaxProof = (value) => {
        setTaxProof(value);
        setTaxProofError("");
    };

    const _handleChangeTaxReference = (value) => {
        setTaxReferenceNumber(value)
        setTaxReferenceNumberError("");
    };


    //handle corrs address change 
    const _handleCorrspondAddressChange = (value) => {
        setCorrespondAddressOne(value);
        setCorrespondAddressError("");
    };

    //handle address 2 change 
    const _handleCorrspondSecondAddressChange = (value) => {
        setCorrespondSecondAddress(value);
        setCorrespondSecondAddressError("");
    };

    //handle select correspond nation
    const _handleCorrespondNation = (value) => {
        setCorrespondNation(value);
        setCorrespondNationError("");

        if (value.id == 251) {
            setCorrespondIndianState({ id: null, name: "" });
            setCorrespondIndianCity({ id: null, name: "" });
        } else {
            setCorrespondState("");
            setCorrespondCity("");
        }
    };

    // handle correspond state change
    const _handleCorrspondStateChange = (value) => {
        setCorrespondState(value);
        setCorrespondStateError("");
    };

    const _handleCorrspondCityChange = (value) => {
        setCorrespondCity(value);
        setCorrespondCityError("");
    }

    const _handleCorrPinCodeChange = (value) => {
        setCorrespondPinCode(value.toUpperCase());
        setCorrespondPinCodeError("");
    };


    //handle select state
    const _handleSelectState = (value) => {
        setSelectedState(value);
        setStateError("");
        // clearing city when state changes
        setSelectedCity({ id: null, name: "" });
    };

    //handle select corrs  state
    const _handleSelectCorrespondIndianState = (value) => {
        setCorrespondIndianState(value);
        setCorrespondStateError("");
        // clearing city when state changes
        setCorrespondIndianCity({ id: null, name: "" });
    };

    //handle select correspond city
    const _handleSelectCorrespondIndianCity = (value) => {
        setCorrespondIndianCity(value);
        setCorrespondCityError("");
    };


    //handle select city
    const _handleSelectCity = (value) => {
        setSelectedCity(value);
        setCityError("");
    };

    const _handleSelectDifferentCorrsAddress = () => {
        setHasDifferentCorrsAddress(prev => !prev);
    };

    // handle upload pan
    const _handleUploadPan = (file) => {
        if (file && file?.target?.files?.length !== 0) {
            const is_file_size_valid = _checkFileSize(file.target.files[0].size);

            if (is_file_size_valid === false) {
                setPanFileError("Maximum file size should be 5mb only");
                return;
            } else {
                setIsFileUpload("PAN");
                _getPresignedUrl(file, 1);

            }

        } else {
            setPanFile(null);
        }
    };

    // handle upload pan back   
    const _handleUploadSignature = (file) => {
        if (file && file?.target?.files?.length !== 0) {
            const is_file_size_valid = _checkFileSize(file.target.files[0].size);

            if (is_file_size_valid === false) {
                setSignFileError("Maximum file size should be 5mb only");
                return;
            } else {
                setIsFileUpload("SIGN");
                _getPresignedUrl(file, 3);
            }

        } else {
            setSignFile(null);
        }
    };

    const _handleUploadPersonImage = (file) => {
        if (file && file?.target?.files?.length !== 0) {
            const is_file_size_valid = _checkFileSize(file.target.files[0].size);

            if (is_file_size_valid === false) {
                setPersonImageFileError("Maximum file size should be 5mb only");
                return;
            } else {
                setIsFileUpload("PERSON_IMAGE");
                _getPresignedUrl(file, 2);

            }

        } else {
            setPersonImageFile(null);
        }
    };


    const _handleFatherNameChange = (value) => {
        setFatherName(value);
        setFatherNameError("");
    };

    const _handleMotherNameChange = (value) => {
        setMotherName(value);
        setMotherNameError("");
    };

    //handle select country
    const _handleSelectCountry = (value) => {
        setSelectedCountry(value);
        setCountryError("");
    };


    const _handleValidateDetails = () => {

        _trackEvent("personal_details_submitted", { screen: "personal_details", flow: "onboarding" });

        // Type === 1 KYC user
        // Type === 2 Non KYC user 
        // Type === 3 NRI KYC user 
        // Type === 4 NRI Non KYC user 

        let errors = [];
        let is_valid = true;




        if (name.trim().length === 0) {
            setNameError("Name cannot be empty");
            is_valid = false;
        }

        if (dob.length === 0) {
            setDobError("Please select your date of birth");
            errors.push("dob");
            is_valid = false;
        }

        if (citizenSelect === 2) {
            if (selectedCountry.length === 0) {
                setCountryError("Please select your country");
                errors.push("country");
                is_valid = false;
            }
        };

        if (taxResidentSelect === 1) {
            if (taxPayingCountry.length === 0) {
                setTaxPayingCountryError("Please select your tax paying country");
                errors.push("tax-paying-country");
                is_valid = false;
            };

            if (taxProof.length === 0) {
                setTaxProofError("Please select tax proof");
                errors.push("tax-proof");
                is_valid = false;
            }

            if (taxReferenceNumber.length === 0) {
                setTaxReferenceNumberError("Please enter tax reference number");
                errors.push("tax-reference");
                is_valid = false;
            }
        };


        if (selectedOccupation.length === 0) {
            setOccupationError("Please specify your occupation type");
            errors.push("occupation");
            is_valid = false;
        };

        if (selectedSource.length === 0) {
            setIncomeSourceError("Please specify your source of income");
            errors.push("source-of-income");
            is_valid = false;
        };

        if (income === 0 || income.length === 0) {
            setIncomeError("Please provide your annual income");
            errors.push("income");
            is_valid = false;
        };


        if (props.kycUser === false) {

            if (fatherName.length === 0) {
                setFatherNameError("Please provide your father's name");
                errors.push("father-name");
                is_valid = false;
            }

            if (motherName.length === 0) {
                setMotherNameError("Please provide your mother's name");
                errors.push("mother-name");
                is_valid = false;
            }
        };


        if (address.trim().length === 0) {
            setAddressError("Please provide your address details");
            errors.push("address-1");
            is_valid = false;
        }

        if (secondAddress.trim().length === 0) {
            setSecondAddressError("Please provide your address details");
            errors.push("address-2");
            is_valid = false;
        }

        if (selectedState.length === 0) {
            setStateError("Please provide your state name");
            errors.push("state");
            is_valid = false;
        }

        if (selectedCity.length === 0) {
            setCityError("Please provide your city name");
            errors.push("city");
            is_valid = false;
        }

        if (pinCode.length === 0 || pinCode.length < 6) {
            setPinCodeError(pinCode.length === 0 ? "Provide your PIN code" : "Provide your full PIN code");
            errors.push("pincode");
            is_valid = false;
        }

        if (hasDifferentCorrsAddress) {

            if (correspondNation.length === 0) {
                setCorrespondNationError("Please provide your country information");
                errors.push("corrs-nationality");
                is_valid = false;
            }
            if (correspondAddressOne.length === 0) {
                setCorrespondAddressError("Please provide your address details")
                errors.push("corrs-address-1");
                is_valid = false;
            }

            if (correspondSecondAddress.length === 0) {
                setCorrespondSecondAddressError("Please provide your additional address details");
                errors.push("corrs-address-2");
                is_valid = false;
            }


            if (correspondPinCode.length === 0 || correspondPinCode.length < 6) {
                if (correspondNation.id == 251 || correspondNation?.length === 0) {
                    setCorrespondPinCodeError(correspondPinCode.length === 0 ? "Provide your correspond PIN code" : "Provide your full correspond PIN code");
                } else {
                    setCorrespondPinCodeError(correspondPinCode.length === 0 ? "Provide your ZIP code code" : "Provide your full ZIP code");
                }
                errors.push("corrs-pincode");
                is_valid = false;
            }

            if (correspondNation.id == 251) {
                if (correspondIndianState.length === 0 || correspondIndianState?.id == null) {
                    setCorrespondStateError("Please provide your correspond state");
                    errors.push("corrs-selected-state");
                    is_valid = false;
                }

                if (correspondIndianCity.length === 0 || correspondIndianCity?.id == null) {
                    setCorrespondCityError("Please provide your correspond city");
                    errors.push("corrs-selected-city");
                    is_valid = false;
                }
            } else {
                if (correspondState.length === 0) {
                    setCorrespondStateError("Please provide your correspond state");
                    errors.push("corrs-state");
                    is_valid = false;
                }

                if (correspondCity.length === 0) {
                    setCorrespondCityError("Please provide your correspond city");
                    errors.push("corrs-city");
                    is_valid = false;
                }


            }


        }

        if (props.kycUser === false) {
            if (panFile === null) {
                setPanFileError("Please upload your PAN card");
                errors.push("pan-card-image");
                is_valid = false;
            }

            if (personImageFile === null) {
                setPersonImageFileError("Please upload your image");
                errors.push("person-proof-image");
                is_valid = false;
            }

            if (signFile === null) {
                setSignFileError("Please upload your signature");
                errors.push("sign-image");
                is_valid = false;
            }
        }

        // Scrolling to first error
        if (errors.length !== 0) {
            let first_err_id = errors[0];
            let element = document.getElementById(first_err_id);
            element.scrollIntoView({ behavior: "smooth" });
        };

        if (is_valid === true) {
            setLoading(true);
            _editPersonalDetails();
        };
    };

    // Back to previous step -Onboarding
    const _handleBackToPrevious = () => {
        props.handleBack(props.isNRI ? 1 : 3); // 1 - Pan verification, 3 - Review Details  
    };


    // ================================ //
    //============= API CALLS ========= //

    //API- Check kyc status
    const _editPersonalDetails = () => {

        let api_version = 2
        let is_post = true;
        let url = 'kyc/update-details';
        let data = {
            "personal_data": {
                "dob": format(dob, "dd-MM-yyyy"),
                "gender_id": genderSelect,
                "is_indian_citizen": citizenSelect === 1 ? true : false,
                "country_id": citizenSelect === 1 ? 251 : selectedCountry.id,
                "is_indian_tax_payer": taxResidentSelect === 2 ? true : false,
                "reference_proof_id": taxResidentSelect === 1 ? taxProof.id : null,
                "tax_reference_number": taxResidentSelect === 1 ? taxReferenceNumber : null,
                "tax_country_id": taxResidentSelect === 1 ? taxPayingCountry.id : null,

                "annual_income": income,
                "occupation_id": selectedOccupation.id,
                "income_source_id": selectedSource.id,
                "marital_status_id": martialSelect,
                "father_name": fatherName,
                "mother_name": motherName,
                "identity_proof_url": panViewUrl,
                "signature_proof_url": signViewUrl,
                "photo_proof_url": personImageViewUrl,
            },
            "address": {
                "permanent_address": {
                    "address_line1": address,
                    "address_line2": secondAddress,
                    "pincode": pinCode,
                    "country_id": 251,
                    "city": selectedCity.name,
                    "state_name": selectedState.name,
                    "state_id": selectedState.id,
                    "city_id": selectedCity.id
                },
                "corresponding_address": hasDifferentCorrsAddress ? {
                    "address_line1": correspondAddressOne,
                    "address_line2": correspondSecondAddress,
                    "pincode": correspondPinCode,
                    "country_id": correspondNation.id,
                    "city": correspondNation.id === 251 ? correspondIndianCity?.name : correspondCity || null,
                    "state_name": correspondNation.id === 251 ? correspondIndianState?.name : correspondState || null,
                    "state_id": correspondNation.id === 251 ? correspondIndianState?.id : null,
                    "city_id": correspondNation.id === 251 ? correspondIndianCity?.id : null
                } : {},
                "is_same_corresponding_address": hasDifferentCorrsAddress === true ? false : true
            }
        };
        let token = sessionStorage.getItem('access_token');

        APIService(is_post, url, data, token, api_version).then((response) => {
            if (response.status_code === 200) {
                _trackEvent("personal_details_completed", { screen: "personal_details", flow: "onboarding" });
                props.handleSuccess();
                toast.dismiss();
                toast.success("Personal details saved successfully", {
                    className: "e-toast",
                });
                // To update signup progress data
                _getSignupProgress();
                _getUserData();
            } else {
                _trackEvent("personal_details_failed", { screen: "personal_details", flow: "onboarding", status_code: response.status_code, error_message: response.message });
                toast.dismiss();
                toast.error(response.message, {
                    className: "e-toast",
                });
            }
            setLoading(false);
        });;
    };

    async function _getPresignedUrl(file, type) {

        let file_name = file.target.files[0].name;

        let is_post = true;
        let url = 'file/upload';
        let data = {
            "type": file.target.files[0].type,
            "file": file_name,
            "is_public": true
        };

        try {
            let response = await APIService(is_post, url, data);
            let aws_URL = '';
            if (response.status_code == 200) {

                aws_URL = response.data.upload_info.url;
                let view_url = response.data.view_info;

                let file_param = new File(
                    [file.target.files[0]],
                    file.target.files[0].name,
                    { type: file.target.files[0].type }
                );

                try {
                    let awsResponse = await awsService(response.data.upload_info.fields, file_param, aws_URL);

                    if (awsResponse.status == 'ok') {

                        if (type === 1) {
                            setPanViewUrl(view_url);
                            setPanFile(file.target.files[0]);
                            setPanFileError("");
                        }
                        if (type === 2) {
                            setPersonImageViewUrl(view_url);
                            setPersonImageFile(file.target.files[0]);
                            setPersonImageFileError("");
                        }
                        if (type === 3) {
                            setSignViewUrl(view_url);
                            setSignFile(file.target.files[0]);
                            setSignFileError("");
                        }

                    } else {
                        toast.dismiss();
                        toast.error(awsResponse.message, { className: "e-toast" });
                    }
                } catch (err) {
                    toast.dismiss();
                    toast.error(err.message, { className: "e-toast" });
                }
            }

            setIsFileUpload("");

        } catch (err) {
            setIsFileUpload("");
        }
    };

    //API- Get state
    const _getState = () => {
        let is_post = false;
        let url = 'general/states';
        let data = null;

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setStateData(response.data);
            } else {
                setStateData([]);
                // If we don't have state data
                // User cannot continue further
                toast.dismiss();
                toast.error("We are facing issue in fetching state data. Please try later", { className: "e-toast" });
            }

        });
    };

    //API- Post City
    const _getCity = (state_id) => {
        let is_post = true;
        let url = 'general/cities';
        let data = {
            "state_id": state_id,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setCityData(response.data);
            } else {
                setCityData([]);
                toast.dismiss();
                toast.error("We are facing issue in fetching city data. Please try later", { className: "e-toast" });
            }

        });
    };


    return (
        <div className="col-sm-10 col-xl-9 mx-auto px-sm-0">
            <div className="e-padding-block-40px-res padding-76px-top padding-150px-bottom position-relative">


                <div className={`${styles.e_onboarding_wrapper_personal_details} e-border-radius-24`}>

                    {
                        dataLoading ?
                            <div className="min-h-100vh text-center mt-5 pt-5" >
                                <img src={Loader}
                                    className='object-fit-contain w-20 width-24px-res'
                                    draggable="false"
                                    alt="loader" />
                            </div> :
                            <>




                                <div className="row flex-column flex-sm-row justify-content-sm-between align-items-sm-center row-gap-2  mb-sm-5 mb-4 ">
                                    <div className="col">
                                        <div className="d-flex gap-3 gap-sm-4">
                                            {/* <Icon icon="arrow-back-left"    
                                                className="cursor-pointer"
                                                size={24}
                                                onClick={_handleBackToPrevious} /> */}
                                            <h1 className="e-text-chinese-black e-font-16-res e-font-20 e-alt-font-poppins e-font-weight-600 line-height-24px mb-0">
                                                Personal Details
                                            </h1>
                                        </div>
                                    </div>

                                    <div className="col-10 col-sm-6 col-lg-4">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="flex-fill progress h-6 e-bg-lavender e-border-radius-16" role="progressbar" aria-label="Success example" aria-valuenow="50" aria-valuemin="0" aria-valuemax="100">
                                                <div className={`${props.kycUser ? "w-33per" : "w-20"} progress-bar progress-bar-bg e-border-radius-16`} ></div>
                                            </div>
                                            <p className='mb-0 e-text-egyptian-blue e-font-12 e-alt-font-poppins e-font-weight-500 line-height-24px'>
                                                Step 1 of {props.kycUser ? 3 : 5}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Personal Information */}
                                <div className="onboarding-section-wrapper">
                                    <div className="row gx-gy-32px">
                                        <div className="col-sm-6 col-lg-4" id='name'>
                                            <CustomTextInput label="Name as per PAN*"
                                                placeholder="Joseph Thomas"
                                                type="text"
                                                floatingLabel
                                                value={name}
                                                errorMsg={nameError}
                                                className="w-100"
                                                handleChange={_handleNameChange} />
                                        </div>

                                        <div className="col-sm-6 col-lg-4" id='dob'>
                                            <DatePicker value={dob}
                                                label="Date of birth*"
                                                floatingLabel
                                                minDate={subYears(new Date(), 100)}
                                                maxDate={subYears(new Date(), 18)}
                                                error={dobError}
                                                onChange={_handleDateChange} />
                                        </div>
                                    </div>

                                    <div className="row gx-gy-32px mt-4">
                                        <div className="col-sm-6 col-lg-4" id='gender'>
                                            <h6 className='margin-12px-bottom e-text-charleston-green e-font-14 e-alt-font-poppins e-font-weight-400'>
                                                Your gender*
                                            </h6>
                                            <div className='d-flex flex-wrap gap-3'>
                                                <RadioGroup data={generalData.genders}
                                                    selected={genderSelect}
                                                    handleSelect={_handleGenderSelect} />
                                            </div>
                                        </div>

                                        <div className="col-sm-6 col-lg-4" id='gender'>
                                            <h6 className='margin-12px-bottom e-text-charleston-green e-font-14 e-alt-font-poppins e-font-weight-400'>
                                                Your martial status*
                                            </h6>
                                            <div className='d-flex flex-wrap gap-3'>
                                                <RadioGroup data={generalData.marital_statuses}
                                                    selected={martialSelect}
                                                    handleSelect={_handleMartialSelect} />
                                            </div>
                                        </div>
                                    </div>

                                </div>

                                {/* Nationality, Tax & Source */}
                                <div className="onboarding-section-wrapper margin-top-32px">
                                    <h2 className="e-text-charleston-green e-font-16 e-alt-font-poppins e-font-weight-600 line-height-24px mb-0">
                                        Nationality, Tax & Source
                                    </h2>

                                    {
                                        props.isNRI === true &&
                                        <div className="row gx-gy-32px mt-4">
                                            <div className="col-sm-6 col-lg-4" id='citizen'>
                                                <h6 className='margin-12px-bottom e-text-charleston-green e-font-14 e-alt-font-poppins e-font-weight-400'>
                                                    Are you an Indian citizen?*
                                                </h6>
                                                <div className='d-flex flex-wrap gap-3'>
                                                    <RadioGroup data={YES_NO_OPTIONS}
                                                        selected={citizenSelect}
                                                        handleSelect={_handleCitizenSelect} />
                                                </div>
                                            </div>
                                            <div className="col-sm-6 col-lg-5" id="tax">
                                                <h6 className='margin-12px-bottom e-text-charleston-green e-font-14 e-alt-font-poppins e-font-weight-400'>
                                                    Are you a tax-paying resident in any other country?*
                                                </h6>
                                                <div className='d-flex flex-wrap gap-3'>
                                                    <RadioGroup data={YES_NO_OPTIONS}
                                                        selected={taxResidentSelect}
                                                        handleSelect={_handleTaxSelect} />
                                                </div>
                                            </div>
                                        </div>
                                    }


                                    {
                                        (citizenSelect === 2 || taxResidentSelect === 1) &&
                                        <div className="row gx-gy-32px mt-4">
                                            {/* Country select */}
                                            {
                                                citizenSelect === 2 &&
                                                <div className="col-sm-6 col-lg-4"
                                                    id='country'>
                                                    <CustomTextInput label="Country*"
                                                        placeholder="Select country"
                                                        dropdown={true}
                                                        type="text"
                                                        isOpen={showCountryModal}
                                                        value={selectedCountry.name}
                                                        errorMsg={countryError}
                                                        readOnly
                                                        floatingLabel
                                                        disabled={countryData ? false : true}
                                                        onClick={() => setShowCountryModal(true)} />
                                                </div>
                                            }

                                            {
                                                taxResidentSelect == 1 &&
                                                <>

                                                    <div className="col-sm-6 col-lg-4" id='tax-paying-country'>
                                                        <CustomTextInput label="Tax paying country*"
                                                            placeholder="Select country"
                                                            dropdown={true}
                                                            type="text"
                                                            floatingLabel
                                                            isOpen={showTaxPayingCountryModal}
                                                            value={taxPayingCountry.name}
                                                            errorMsg={taxPayingCountryError}
                                                            readOnly
                                                            disabled={countryData ? false : true}
                                                            onClick={() => setShowTaxPayingCountryModal(true)} />
                                                    </div>
                                                    <div className="col-sm-6 col-lg-4" id='tax-proof'>
                                                        <CustomTextInput label="Tax proof*"
                                                            dropdown={true}
                                                            placeholder="Select tax proof"
                                                            type="text"
                                                            floatingLabel
                                                            isOpen={showTaxProofModal}
                                                            value={taxProof?.display_name}
                                                            errorMsg={taxProofError}
                                                            readOnly
                                                            onClick={() => setShowTaxProofModal(true)} />
                                                    </div>
                                                    <div className="col-sm-6 col-lg-4" id='tax-reference'>
                                                        <CustomTextInput label="Tax reference number*"
                                                            placeholder="FHDC28738"
                                                            type="text"
                                                            floatingLabel
                                                            value={taxReferenceNumber}
                                                            errorMsg={taxReferenceNumberError}
                                                            handleChange={_handleChangeTaxReference} />
                                                    </div>
                                                </>
                                            }

                                        </div>
                                    }

                                    <div className="row gx-gy-32px margin-top-32px">
                                        <div className="col-sm-6 col-lg-4" >
                                            <div id='occupation'
                                                className='position-relative'>
                                                <CustomTextInput label="Occupation*"
                                                    dropdown={true}
                                                    placeholder="Select occupation"
                                                    type="text"
                                                    floatingLabel
                                                    value={selectedOccupation?.display_name}
                                                    errorMsg={occupationError}
                                                    readOnly
                                                    isOpen={showOccupationModal}
                                                    className="w-100"
                                                    onClick={() => setShowOccupationModal(true)} />
                                            </div>
                                        </div>

                                        <div className="col-sm-6 col-lg-4">
                                            {/* source of Income */}
                                            <div id='source-of-income'
                                                className='position-relative'>
                                                <CustomTextInput label="Source of income*"
                                                    dropdown={true}
                                                    placeholder="Select source of income"
                                                    type="text"
                                                    floatingLabel
                                                    isOpen={showSourceOfIncomeModal}
                                                    value={selectedSource.display_name}
                                                    errorMsg={incomeSourceError}
                                                    readOnly
                                                    className="w-100 px-3 padding-12px-top padding-12px-bottom cursor-pointer "
                                                    onClick={() => setShowSourceOfIncomeModal(true)} />
                                            </div>
                                        </div>

                                        <div className="col-sm-6 col-lg-4">
                                            <div id='income'>
                                                <CustomAmountInput label='Annual income*'
                                                    placeholder={0}
                                                    value={income}
                                                    floatingLabel
                                                    errorMsg={incomeError}
                                                    maxLength={8}
                                                    handleChange={(value) => _handleIncome(value)} />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Nationality, Tax & Source */}
                                {
                                    props.kycUser === false &&
                                    <div className="onboarding-section-wrapper margin-top-32px">
                                        <h2 className="e-text-charleston-green e-font-16 e-alt-font-poppins e-font-weight-600 line-height-24px mb-0">
                                            Parents details
                                        </h2>


                                        <div className="row gx-gy-32px margin-top-32px">
                                            <div className="col-sm-6 col-lg-4" id='father-name'>
                                                <CustomTextInput label="Fathers name*"
                                                    placeholder="Doe John"
                                                    type="text"
                                                    floatingLabel
                                                    value={fatherName}
                                                    errorMsg={fatherNameError}
                                                    className="w-100"
                                                    handleChange={_handleFatherNameChange} />
                                            </div>
                                            <div className="col-sm-6 col-lg-4" id='mother-name'>
                                                <CustomTextInput label="Mothers name*"
                                                    placeholder="Marry John"
                                                    type="text"
                                                    floatingLabel
                                                    value={motherName}
                                                    errorMsg={motherNameError}
                                                    className="w-100"
                                                    handleChange={_handleMotherNameChange} />
                                            </div>
                                        </div>
                                    </div>
                                }

                                {/* Address Details */}
                                <div className="onboarding-section-wrapper margin-top-32px">
                                    {
                                        props.isNRI ?
                                            <h2 className="e-text-charleston-green e-font-16 e-alt-font-poppins e-font-weight-600 line-height-24px mb-0">
                                                Permanent Address Details
                                            </h2>
                                            :
                                            <h2 className="e-text-charleston-green e-font-16 e-alt-font-poppins e-font-weight-600 line-height-24px mb-0">
                                                Address Details
                                            </h2>
                                    }


                                    <div className="row gx-gy-32px mt-4">
                                        <div className="col-sm-6 col-lg-4">
                                            {/* address line 1 */}
                                            <div id='address-1'>
                                                <CustomTextInput label={"Address line 1*"}
                                                    placeholder="202,Madhav Apts, jawar road......."
                                                    type="text"
                                                    inputType="alphanumeric"
                                                    floatingLabel
                                                    maxLength={250}
                                                    value={address}
                                                    errorMsg={addressError}
                                                    handleChange={_handleAddressChange} />
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-lg-4">
                                            {/* address line 2 */}
                                            <div id='address-2'>
                                                <CustomTextInput label={"Address line 2*"}
                                                    placeholder="202,Madhav Apts, jawar road......."
                                                    type="text"
                                                    inputType="alphanumeric"
                                                    maxLength={250}
                                                    floatingLabel
                                                    value={secondAddress}
                                                    errorMsg={secondAddressError}
                                                    handleChange={_handleSecondAddressChange} />
                                            </div>
                                        </div>

                                        <div className="col-sm-6 col-lg-4">
                                            <div id='nationality'
                                                className='position-relative pe-none'>
                                                <CustomTextInput label="Country*"
                                                    placeholder="India"
                                                    type="text"
                                                    floatingLabel
                                                    value="India"
                                                    readOnly
                                                    className="pe-none cursor-default opacity-75" />
                                            </div>
                                        </div>

                                        <div className='col-sm-6 col-lg-4'>
                                            <div id='state'
                                                className='position-relative'>
                                                <CustomTextInput label={"State*"}
                                                    placeholder="Karnataka"
                                                    dropdown={true}
                                                    type="text"
                                                    floatingLabel
                                                    value={selectedState.name}
                                                    errorMsg={stateError}
                                                    readOnly
                                                    isOpen={showSuggestModal}
                                                    disabled={stateData.length === 0 ? true : false}
                                                    onClick={() => _handleSuggestionModal(1)} />
                                            </div>
                                        </div>

                                        <div className='col-sm-6 col-lg-4'>
                                            <div id='city'
                                                className={`${selectedState.name ? "" : "e-disable"} position-relative`}>
                                                <CustomTextInput label="City*"
                                                    placeholder="Bangalore"
                                                    dropdown={true}
                                                    type="text"
                                                    floatingLabel
                                                    value={selectedCity.name}
                                                    errorMsg={cityError}
                                                    readOnly
                                                    isOpen={showSuggestModal}
                                                    disabled={selectedState.id && cityData.length !== 0 ? false : true}
                                                    onClick={() => _handleSuggestionModal(2)} />


                                            </div>
                                        </div>

                                        <div className="col-sm-6 col-lg-4">
                                            <div id='pincode'>
                                                <CustomTextInput label={"Pincode*"}
                                                    placeholder="670676"
                                                    type="number"
                                                    inputType="pincode"
                                                    floatingLabel
                                                    value={pinCode}
                                                    errorMsg={pinCodeError}
                                                    maxLength={6}
                                                    className="w-100 px-3 padding-12px-top padding-12px-bottom"
                                                    handleChange={_handlePinChange} />
                                            </div>
                                        </div>


                                    </div>
                                    {
                                        props.isNRI === false ?
                                            <div className="col-auto">
                                                <div className='d-flex align-items-center gap-2 mt-4'>
                                                    <img src={hasDifferentCorrsAddress ? ActiveCheckbox : Inactive}
                                                        draggable={false}
                                                        className='cursor-pointer'
                                                        alt='checkbox'
                                                        onClick={_handleSelectDifferentCorrsAddress} />
                                                    <p className='mb-0 e-text-raisin-black_80 e-font-14 e-alt-font-poppins e-font-weight-500 line-height-20px   '>
                                                        Have different correspondence address
                                                    </p>
                                                </div>
                                            </div>
                                            :
                                            null
                                    }


                                    {
                                        hasDifferentCorrsAddress &&
                                        <div className="margin-top-32px">
                                            {
                                                props.isNRI == true &&

                                                <h2 className="e-text-charleston-green e-font-16 e-alt-font-poppins e-font-weight-600 line-height-24px mb-4">
                                                    Overseas Address Details
                                                </h2>

                                            }

                                            <div className="row gx-gy-32px">

                                                {/* address line 1 */}
                                                <div className="col-sm-6 col-lg-4">
                                                    <div id='corrs-address-1'>
                                                        <CustomTextInput label="Address line 1*"
                                                            placeholder="202,Madhav Apts, jawar road......."
                                                            type="text"
                                                            inputType="alphanumeric"
                                                            floatingLabel
                                                            maxLength={250}
                                                            value={correspondAddressOne}
                                                            errorMsg={correspondAddressError}
                                                            className="w-100"
                                                            handleChange={_handleCorrspondAddressChange} />
                                                    </div>
                                                </div>

                                                {/* address line 2 */}
                                                <div className="col-sm-6 col-lg-4">
                                                    <div id='corrs-address-2' >
                                                        <CustomTextInput label="Address line 2*"
                                                            placeholder="202,Madhav Apts, jawar road......."
                                                            type="text"
                                                            inputType="alphanumeric"
                                                            floatingLabel
                                                            maxLength={250}
                                                            value={correspondSecondAddress}
                                                            errorMsg={correspondSecondAddressError}
                                                            handleChange={_handleCorrspondSecondAddressChange} />
                                                    </div>
                                                </div>

                                                <div className="col-sm-6 col-lg-4">
                                                    <div id='corrs-nationality' className='position-relative'>
                                                        <CustomTextInput label="Country*"
                                                            placeholder="India"
                                                            dropdown={true}
                                                            type="text"
                                                            floatingLabel
                                                            isOpen={showSuggestModal}
                                                            value={correspondNation.name}
                                                            errorMsg={correspondNationError}
                                                            readOnly
                                                            className="w-100 cursor-pointer"
                                                            onClick={() => _handleSuggestionModal(3)} />
                                                    </div>
                                                </div>

                                                <div className='col-sm-6 col-lg-4'>
                                                    {
                                                        correspondNation?.id === 251 ?
                                                            <div id='corrs-selected-state' className='position-relative'>
                                                                <CustomTextInput label={"State *"}
                                                                    placeholder="Karnataka"
                                                                    id="corrs-state-select-input"
                                                                    dropdown={true}
                                                                    type="text"
                                                                    floatingLabel
                                                                    value={correspondIndianState?.name}
                                                                    errorMsg={correspondStateError}
                                                                    readOnly
                                                                    isOpen={showSuggestModal}
                                                                    disabled={stateData.length === 0 ? true : false}
                                                                    onClick={() => _handleSuggestionModal(5)} />
                                                            </div>
                                                            :
                                                            <div id='corrs-state'>
                                                                <CustomTextInput label="State*"
                                                                    id="corrs-state-input"
                                                                    placeholder="Kerala"
                                                                    type="text"
                                                                    inputType="alphanumeric"
                                                                    maxLength={100}
                                                                    floatingLabel
                                                                    value={correspondState}
                                                                    errorMsg={correspondStateError}
                                                                    className="w-100"
                                                                    handleChange={_handleCorrspondStateChange} />
                                                            </div>

                                                    }
                                                </div>

                                                {/*select city*/}
                                                <div className='col-sm-6 col-lg-4'>
                                                    {
                                                        correspondNation?.id === 251 ?
                                                            <div id='corrs-selected-city' className='position-relative'>
                                                                <CustomTextInput label={"City *"}
                                                                    placeholder="Kannur"
                                                                    id="corrs-city-select-input"
                                                                    dropdown={true}
                                                                    type="text"
                                                                    floatingLabel
                                                                    maxLength={100}
                                                                    value={correspondIndianCity?.name}
                                                                    errorMsg={correspondCityError}
                                                                    readOnly
                                                                    isOpen={showSuggestModal}
                                                                    disabled={correspondIndianState.id && cityData.length !== 0 ? false : true}
                                                                    onClick={() => _handleSuggestionModal(6)} />
                                                            </div>
                                                            :
                                                            <div id='corrs-city'>
                                                                <CustomTextInput label="City*"
                                                                    placeholder="Kannur"
                                                                    type="text"
                                                                    inputType="alphanumeric"
                                                                    floatingLabel
                                                                    value={correspondCity}
                                                                    errorMsg={correspondCityError}
                                                                    className="w-100 px-3 padding-12px-top padding-12px-bottom cursor-pointer"

                                                                    handleChange={_handleCorrspondCityChange} />
                                                            </div>
                                                    }
                                                </div>

                                                {/* ZIP code*/}
                                                <div className="col-sm-6 col-lg-4">
                                                    <div id='corrs-pincode'>
                                                        <CustomTextInput label={correspondNation?.id === 251 ? "Pincode*" : "Zipcode*"}
                                                            placeholder="670676"
                                                            type={correspondNation?.id === 251 ? "number" : "text"}
                                                            inputType={correspondNation?.id === 251 ? "pincode" : "alphanumeric"}
                                                            floatingLabel
                                                            value={correspondPinCode}
                                                            errorMsg={correspondPinCodeError}
                                                            maxLength={6}
                                                            handleChange={_handleCorrPinCodeChange} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>

                                {/* Attach document */}
                                {
                                    props.kycUser === false &&
                                    <div className="onboarding-section-wrapper margin-top-32px">
                                        <h2 className="e-text-charleston-green e-font-16 e-alt-font-poppins e-font-weight-600 line-height-24px mb-0">
                                            Attach Documents
                                        </h2>
                                        <div className="row gx-gy-32px margin-top-32px">

                                            <div className="col-sm-6 col-lg-4">
                                                {/* Upload pan card */}
                                                <div id='pan-card-image'>
                                                    <FilePicker id="pan-card"
                                                        label="Upload PAN card Image (max. 5mb)*"
                                                        type="file"
                                                        className="w-100"
                                                        accept=".png, .jpg, .jpeg"
                                                        isFileUploading={isFileUpload === "PAN" ? true : false}
                                                        file={panFile}
                                                        error={panFileError}
                                                        selectFile={_handleUploadPan} />
                                                </div>
                                            </div>


                                            <div className="col-sm-6 col-lg-4">
                                                {/* Upload person image */}
                                                <div id='person-proof-image'>
                                                    <FilePicker id="person-image"
                                                        label="Upload your image (max. 5mb)*"
                                                        type="file"
                                                        className="w-100"
                                                        accept=".png, .jpg, .jpeg"
                                                        isFileUploading={isFileUpload === "PERSON_IMAGE" ? true : false}
                                                        file={personImageFile}
                                                        error={personImageFileError}
                                                        selectFile={_handleUploadPersonImage} />
                                                </div>
                                            </div>

                                            <div className="col-sm-6 col-lg-4">
                                                {/* Upload pan card */}
                                                <div id='sign-image'>
                                                    <FilePicker id="signature-image"
                                                        label="Upload signature image (max. 5mb)*"
                                                        type="file"
                                                        className="w-100"
                                                        accept=".png, .jpg, .jpeg"
                                                        isFileUploading={isFileUpload === "SIGN" ? true : false}
                                                        file={signFile}
                                                        error={signFileError}
                                                        selectFile={_handleUploadSignature} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }


                                <div className="row justify-content-end margin-top-32px">
                                    <div className="col-sm-6 col-lg-4">
                                        <PrimaryButton name={"Update"}
                                            loading={loading}
                                            disabled={loading}
                                            className={`w-100 padding-12px-tb e-font-16 padding-8px-tb-res`}
                                            onPress={_handleValidateDetails} />
                                    </div>
                                </div>
                            </>
                    }
                </div>
            </div>


            {/* Modals */}
            {/* occupation modal*/}
            {
                showOccupationModal === true &&
                <SelectionSuggestModal title="Choose an occupation"
                    data={generalData.occupation}
                    type={1}
                    id='select-occupation-modal'
                    selectedOption={selectedOccupation}
                    onSelectOption={_handleSelectOccupation}
                    onModalClose={() => setShowOccupationModal(false)} />
            }


            {/* source of income select */}
            {
                showSourceOfIncomeModal &&
                <SelectionSuggestModal title="Choose a source of income"
                    data={generalData.source_of_wealths}
                    type={2}
                    id='select-source-modal'
                    selectedOption={selectedSource}
                    onSelectOption={_handleSelectSource}
                    onModalClose={() => setShowSourceOfIncomeModal(false)} />
            }

            {/* State modal*/}
            {
                showSuggestModal && modalType === 1 &&
                <SelectionSuggestModal title="Choose a state"
                    data={stateData}
                    type={3}
                    search={true}
                    id='select-state-modal'
                    searchPlaceholder={"Eg : Karnataka"}
                    selectedOption={selectedState}
                    onSelectOption={_handleSelectState}
                    onModalClose={() => setShowSuggestModal(false)} />
            }

            {/* city modal */}
            {
                showSuggestModal && modalType === 2 &&
                <SelectionSuggestModal title="Choose a city"
                    data={cityData}
                    type={4}
                    search={true}
                    searchPlaceholder={"Eg : Bangalore"}
                    id='select-city-modal'
                    selectedOption={selectedCity}
                    onSelectOption={_handleSelectCity}
                    onModalClose={() => setShowSuggestModal(false)} />
            }


            {/* correspond Address */}

            {
                showSuggestModal && modalType === 3 &&
                <SelectionSuggestModal title="Choose a country"
                    data={props.isNRI ? countryData?.countries : countryData?.countries_for_address}
                    search={true}
                    searchPlaceholder={"Eg : America"}
                    type={5}
                    id='select-correspond-country-modal'
                    selectedOption={correspondNation}
                    onSelectOption={_handleCorrespondNation}
                    onModalClose={() => setShowSuggestModal(false)} />
            }

            {/* State modal*/}
            {
                showSuggestModal && modalType === 5 &&
                <SelectionSuggestModal title="Choose a state"
                    data={stateData}
                    type={7}
                    search={true}
                    id='select-corrs-state-modal'
                    searchPlaceholder={"Eg : Karnataka"}
                    selectedOption={correspondIndianState}
                    onSelectOption={_handleSelectCorrespondIndianState}
                    onModalClose={() => setShowSuggestModal(false)} />
            }

            {/* State modal*/}
            {
                showSuggestModal && modalType === 6 &&
                <SelectionSuggestModal title="Choose a city"
                    data={cityData}
                    type={8}
                    search={true}
                    id='select-corrs-city-modal'
                    searchPlaceholder={"Eg : Bangalore"}
                    selectedOption={correspondIndianCity}
                    onSelectOption={_handleSelectCorrespondIndianCity}
                    onModalClose={() => setShowSuggestModal(false)} />
            }

            {/* Tax paying country select modal */}
            {
                showTaxPayingCountryModal === true &&
                <SelectionSuggestModal title="Choose a tax paying country"
                    data={countryData.countries}
                    type={9}
                    search={true}
                    searchPlaceholder={"Eg : America"}
                    id='select-tax-paying-country-modal'
                    selectedOption={taxPayingCountry}
                    onSelectOption={_handleSelectTaxPayingCountry}
                    onModalClose={() => setShowTaxPayingCountryModal(false)} />
            }

            {/* Tax proof modal*/}
            {
                showTaxProofModal === true &&
                <SelectionSuggestModal title="Choose a tax proof"
                    data={generalData.fatca_verification}
                    type={10}
                    id='select-tax-proof-modal'
                    selectedOption={taxProof}
                    onSelectOption={_handleSelectTaxProof}
                    onModalClose={() => setShowTaxProofModal(false)} />
            }

            {
                showCountryModal === true &&
                <SelectionSuggestModal title="Choose a country"
                    data={countryData.countries}
                    type={11}
                    search={true}
                    searchPlaceholder={"Eg : America"}
                    id='select-country-modal'
                    selectedOption={selectedCountry}
                    onSelectOption={_handleSelectCountry}
                    onModalClose={() => setShowCountryModal(false)} />
            }

        </div>
    )
}

export default PersonalDetails;